import React, { useState, useEffect } from 'react';

const PortfolioPuzzle = ({ isDarkMode = true }) => {
    // State management
    const [gameState, setGameState] = useState('intro');
    const [currentDifficulty, setCurrentDifficulty] = useState('easy');
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [message, setMessage] = useState('');
    const [showExplanation, setShowExplanation] = useState(false);

    // Questions data
    const QUESTIONS = {
        easy: [
            {
                question: "Which color scheme would be most suitable for a children's educational app?",
                options: [
                    {
                        id: 'a',
                        text: "Bright, primary colors with high contrast",
                        icon: "🌈",
                        isCorrect: true,
                        explanation: "Primary colors are engaging for children and high contrast helps with visual recognition and learning."
                    },
                    {
                        id: 'b',
                        text: "Monochromatic grays",
                        icon: "⚫",
                        isCorrect: false,
                        explanation: "Monochromatic schemes might be too subtle for children and miss opportunities for engagement."
                    },
                    {
                        id: 'c',
                        text: "Dark mode with neon accents",
                        icon: "✨",
                        isCorrect: false,
                        explanation: "This combination could be straining for young eyes and might not create a welcoming learning environment."
                    }
                ]
            },
            {
                question: "What's the best way to indicate a button is interactive on a website?",
                options: [
                    {
                        id: 'a',
                        text: "Hover state with subtle animation",
                        icon: "👆",
                        isCorrect: true,
                        explanation: "Subtle hover animations provide clear feedback without being distracting."
                    },
                    {
                        id: 'b',
                        text: "Blinking text",
                        icon: "📝",
                        isCorrect: false,
                        explanation: "Blinking elements can be distracting and may violate accessibility guidelines."
                    },
                    {
                        id: 'c',
                        text: "No visual feedback",
                        icon: "⬜",
                        isCorrect: false,
                        explanation: "Lack of feedback can leave users uncertain about what's interactive."
                    }
                ]
            }
        ],
        medium: [
            {
                question: "You notice users are abandoning your app's onboarding flow. What's the first step to address this?",
                options: [
                    {
                        id: 'a',
                        text: "Analyze drop-off points in analytics",
                        icon: "📊",
                        isCorrect: true,
                        explanation: "Understanding where users leave helps identify specific pain points in the flow."
                    },
                    {
                        id: 'b',
                        text: "Remove all onboarding steps",
                        icon: "❌",
                        isCorrect: false,
                        explanation: "Eliminating onboarding entirely might leave users without necessary guidance."
                    },
                    {
                        id: 'c',
                        text: "Add more animations",
                        icon: "🎭",
                        isCorrect: false,
                        explanation: "Adding visual elements without addressing the core issue won't solve the abandonment problem."
                    }
                ]
            },
            {
                question: "What's the best approach for handling form validation?",
                options: [
                    {
                        id: 'a',
                        text: "Real-time validation with clear feedback",
                        icon: "✅",
                        isCorrect: true,
                        explanation: "Immediate feedback helps users correct errors as they type, reducing frustration."
                    },
                    {
                        id: 'b',
                        text: "Validate only on submission",
                        icon: "📝",
                        isCorrect: false,
                        explanation: "Waiting until submission can lead to user frustration if multiple errors are found."
                    },
                    {
                        id: 'c',
                        text: "No validation",
                        icon: "🚫",
                        isCorrect: false,
                        explanation: "Lack of validation can lead to data quality issues and user errors."
                    }
                ]
            }
        ],
        hard: [
            {
                question: "Your e-commerce site's user testing shows that people like the new design but sales have dropped. What's your next step?",
                options: [
                    {
                        id: 'a',
                        text: "Conduct task completion analysis",
                        icon: "🔍",
                        isCorrect: true,
                        explanation: "This helps identify if aesthetic improvements inadvertently created friction in the purchase flow."
                    },
                    {
                        id: 'b',
                        text: "Revert to old design",
                        icon: "⏮️",
                        isCorrect: false,
                        explanation: "Reverting without understanding the root cause might miss opportunities for improvement."
                    },
                    {
                        id: 'c',
                        text: "Add more products",
                        icon: "➕",
                        isCorrect: false,
                        explanation: "Adding content doesn't address potential usability issues in the purchase flow."
                    }
                ]
            },
            {
                question: "What's the most effective strategy for implementing a major navigation change in a complex web application?",
                options: [
                    {
                        id: 'a',
                        text: "Gradual rollout with user behavior tracking",
                        icon: "📈",
                        isCorrect: true,
                        explanation: "This approach minimizes risk and allows for data-driven adjustments based on user behavior."
                    },
                    {
                        id: 'b',
                        text: "Immediate full release",
                        icon: "🚀",
                        isCorrect: false,
                        explanation: "A sudden change can disrupt user workflows and might not allow for proper testing."
                    },
                    {
                        id: 'c',
                        text: "Email announcement only",
                        icon: "📧",
                        isCorrect: false,
                        explanation: "Communication alone doesn't address the potential impact on user experience."
                    }
                ]
            }
        ]
    };

    useEffect(() => {
        const total = Object.values(QUESTIONS).reduce((sum, questions) => sum + questions.length, 0);
        setTotalQuestions(total);
    }, []);

    const startGame = () => {
        setGameState('playing');
        setCurrentDifficulty('easy');
        setCurrentQuestionIndex(0);
        setScore(0);
        setMessage('');
    };

    const getCurrentQuestion = () => QUESTIONS[currentDifficulty][currentQuestionIndex];

    const handleAnswer = (option) => {
        if (selectedAnswer !== null) return;

        setSelectedAnswer(option.id);
        setShowExplanation(true);

        if (option.isCorrect) {
            setScore(score + 1);
            setMessage('Correct! 🎉');
        } else {
            setMessage('Not quite right. Keep learning! 💡');
        }

        setTimeout(() => moveToNextQuestion(), 2000);
    };

    const moveToNextQuestion = () => {
        setSelectedAnswer(null);
        setShowExplanation(false);
        setMessage('');

        if (currentQuestionIndex < QUESTIONS[currentDifficulty].length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else if (currentDifficulty === 'easy') {
            setCurrentDifficulty('medium');
            setCurrentQuestionIndex(0);
        } else if (currentDifficulty === 'medium') {
            setCurrentDifficulty('hard');
            setCurrentQuestionIndex(0);
        } else {
            setGameState('success');
        }
    };

    const getScoreMessage = () => {
        const percentage = (score / totalQuestions) * 100;
        if (percentage === 100) return "Perfect score! You're a UX master! 🌟";
        if (percentage >= 80) return "Excellent work! Your UX knowledge is strong! 💪";
        if (percentage >= 60) return "Good job! Keep learning and growing! 📚";
        return "Keep practicing! Every challenge is a learning opportunity! 🎯";
    };

    return (
        <div className="mt-12 sm:mt-16 md:mt-24 lg:mt-32">
            {/* Header */}
            <div className="px-4 sm:px-6 md:px-8 lg:px-32">
                <div className="max-w-3xl mb-12 sm:mb-16">
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-mono tracking-tight mb-8">Design Quiz</h2>
                    <p className="font-mono text-sm sm:text-base text-gray-400 leading-relaxed mb-4">
                        Think you've got what it takes to solve real-world UX puzzles? From color theory to complex user flows,
                        let's see if you can spot the best design decisions! 🎨✨
                    </p>
                    <p className="font-mono text-sm text-gray-400">
                        No pressure though – it's all about learning and having fun! Even the greatest designers started somewhere. 😊
                    </p>
                </div>
            </div>

            {/* Game Content */}
            <div className="px-4 sm:px-6 md:px-8 lg:px-32">
                <div className="max-w-4xl mx-auto">
                    {gameState === 'intro' && (
                        <div className="space-y-6">
                            <button
                                onClick={startGame}
                                className={`w-full py-6 rounded-xl font-mono text-sm transition-all duration-300 relative overflow-hidden
                                    ${isDarkMode ? 'bg-[#111111] hover:bg-[#1a2030]' : 'bg-gray-100 hover:bg-gray-50'}`}
                            >
                                <div className="relative z-10">
                                    <div className="text-lg mb-2">Ready to Test Your Design Superpowers? 🦸‍♂️</div>
                                    <div className="text-gray-400">Click to start the challenge!</div>
                                </div>
                            </button>
                            <div className="text-center font-mono text-sm text-gray-400">
                                Pro tip: Trust your instincts, but remember – every designer was once a beginner! 🌱
                            </div>
                        </div>
                    )}

                    {gameState === 'playing' && (
                        <div className="space-y-8">
                            {/* Progress Display */}
                            <div className="flex justify-between items-center font-mono text-sm mb-8">
                                <div className="flex items-center gap-2">
                                    <span className={`px-3 py-1 rounded-lg text-xs
                                        ${currentDifficulty === 'easy' ? 'bg-green-500' :
                                        currentDifficulty === 'medium' ? 'bg-yellow-500' : 'bg-red-500'} 
                                        text-white`}>
                                        {currentDifficulty.toUpperCase()} LEVEL
                                    </span>
                                </div>
                                <div className="text-gray-400">Score: {score}/{totalQuestions}</div>
                            </div>

                            {/* Question */}
                            <div className="mb-8">
                                <div className="font-mono text-sm mb-2 text-gray-400">
                                    Question {currentQuestionIndex + 1} of {QUESTIONS[currentDifficulty].length}
                                </div>
                                <div className="font-mono text-lg">
                                    {getCurrentQuestion().question}
                                </div>
                            </div>

                            {/* Options */}
                            <div className="space-y-3">
                                {getCurrentQuestion().options.map((option, index) => (
                                    <button
                                        key={option.id}
                                        onClick={() => handleAnswer(option)}
                                        disabled={selectedAnswer !== null}
                                        className={`w-full p-4 rounded-xl font-mono text-sm transition-all duration-300 text-left
                                            ${isDarkMode ? 'bg-[#111111] hover:bg-[#1a2030]' : 'bg-gray-100 hover:bg-gray-50'}
                                            ${selectedAnswer === option.id && option.isCorrect ? 'ring-2 ring-green-500' : ''}
                                            ${selectedAnswer === option.id && !option.isCorrect ? 'ring-2 ring-red-500' : ''}
                                            ${selectedAnswer !== null ? 'cursor-default' : 'cursor-pointer'}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className={`w-6 h-6 flex items-center justify-center rounded-lg
                                                ${selectedAnswer === option.id ? 'bg-blue-500 text-white' :
                                                isDarkMode ? 'bg-[#1a2030]' : 'bg-gray-200'}`}>
                                                {String.fromCharCode(65 + index)}
                                            </span>
                                            {option.text}
                                        </div>
                                        {showExplanation && selectedAnswer === option.id && (
                                            <p className="text-sm mt-3 pl-9 text-gray-400">
                                                {option.explanation}
                                            </p>
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* Message */}
                            {message && (
                                <div className="text-center font-mono text-gray-400 animate-fade-in">
                                    {message}
                                </div>
                            )}
                        </div>
                    )}

                    {gameState === 'success' && (
                        <div className="text-center space-y-6 animate-fade-in">
                            <div className="font-mono text-2xl mb-4">
                                Challenge Complete! 🎉
                            </div>
                            <div className="font-mono text-4xl mb-8">
                                Final Score: {score}/{totalQuestions}
                            </div>
                            <p className="font-mono text-sm text-gray-400 mb-8">
                                {getScoreMessage()}
                            </p>
                            <button
                                onClick={startGame}
                                className={`px-8 py-4 rounded-xl font-mono text-sm transition-all duration-300
                                    animate-pulse hover:animate-none
                                    ${isDarkMode ? 'bg-[#111111] hover:bg-[#1a2030]' : 'bg-gray-100 hover:bg-gray-50'}`}
                            >
                                Try Again
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PortfolioPuzzle;